"use client";

import { useEffect } from "react";
import { useParams } from "next/navigation";
import { I18nextProvider } from "react-i18next";

import i18n from "@/i18n";
import { StoreProvider } from "@/store";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  setLanguage,
  supportedLanguages,
  type SupportedLanguage,
} from "@/store/slices/languageSlice";
import DarkModeClientEffect from "@/components/ui/DarkModeClientEffect";

function LanguageWatcher() {
  const lang = useAppSelector((state) => state.language.current);

  useEffect(() => {
    if (lang && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang]);

  return null;
}

function LanguageRouteSync() {
  const dispatch = useAppDispatch();
  const storedLang = useAppSelector((state) => state.language.current);
  const params = useParams();

  const rawLang = params?.lang ? (Array.isArray(params.lang) ? params.lang[0] : params.lang) : "en";
  const candidate =
    typeof rawLang === "string" &&
    supportedLanguages.includes(rawLang as SupportedLanguage)
      ? (rawLang as SupportedLanguage)
      : "en";

  useEffect(() => {
    if (candidate !== storedLang) {
      dispatch(setLanguage(candidate));
    }
  }, [candidate, storedLang, dispatch]);

  return null;
}

export default function Providers({ children }: { children: React.ReactNode }) {
  return (
    <StoreProvider>
      <LanguageRouteSync />
      <I18nextProvider i18n={i18n}>
        <LanguageWatcher />
        <DarkModeClientEffect />
        {children}
      </I18nextProvider>
    </StoreProvider>
  );
}
